import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors } from '@recruitee/account-data';
import type { FullCandidate } from '@recruitee/candidate-types';
import { SubscriptionService } from '@recruitee/company-logic';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { FeatureDiscoveryActions } from '@recruitee/feature-discovery-data';
import { I18nService } from '@recruitee/i18n';
import { EnabledIntegrationKindsSelectors } from '@recruitee/integrations-data';
import { IntegrationKinds } from '@recruitee/integrations-types';
import { WindowRef } from '@recruitee/user-interface';

@Injectable({ providedIn: 'root' })
export class KiwiHrPromoService {
  private readonly store = inject(Store);
  private readonly windowRef = inject(WindowRef);
  private readonly i18n = inject(I18nService);
  private readonly hasPromoteKiwiHR = injectDevFlag(DevFlag.PromoteKiwiHR);
  private readonly isTrial = inject(SubscriptionService).isTrial();

  private readonly wasClosed$ = this.store.select(
    CurrentUserSelectors.featureDiscovery({ feature: 'promoteKiwihrClosed' }),
  );
  private readonly canManageCandidates$ = this.store.select(
    AbilitiesSelectors.hasOne(Ability.ManageCandidates),
  );
  private readonly isKiwiHREnabled$ = this.store.select(
    EnabledIntegrationKindsSelectors.hasOne(IntegrationKinds.KIWI_HR),
  );

  public isPromoVisible(candidate$: Observable<FullCandidate | null>) {
    if (!this.hasPromoteKiwiHR || this.isTrial) return of(false);

    return combineLatest([
      this.wasClosed$,
      this.canManageCandidates$,
      this.isKiwiHREnabled$,
      candidate$,
    ]).pipe(
      map(
        ([wasClosed, canManageCandidates, isKiwiHREnabled, candidate]) =>
          !wasClosed &&
          canManageCandidates &&
          !isKiwiHREnabled &&
          (!candidate || candidate.isHired),
      ),
    );
  }

  public markAsClosed() {
    this.store.dispatch(FeatureDiscoveryActions.update({ payload: { promoteKiwihrClosed: true } }));
  }

  public open() {
    this.windowRef.open(this.i18n.translate('rti.kiwi_hr.promo.cta_link_dashboard'), '_blank');
  }
}
